import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Palette, Heart, Sparkles, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function CreativeExpressionGuide() {
  const navigate = useNavigate();

  const creativeActivities = [
    { activity: "Adult Coloring", level: "Beginner", benefit: "Meditative, accessible, no skill needed" },
    { activity: "Photography Walk", level: "Beginner", benefit: "See world differently, phone camera is fine" },
    { activity: "Journaling/Poetry", level: "Beginner", benefit: "Process emotions, no rules" },
    { activity: "Cooking without Recipe", level: "Intermediate", benefit: "Creative, practical, tasty results" },
    { activity: "Dancing", level: "Any", benefit: "Physical release, joy, embodied creativity" },
    { activity: "Flower Arranging", level: "Beginner", benefit: "Beauty, mindfulness, tangible result" },
    { activity: "Pottery/Clay", level: "Beginner", benefit: "Tactile, grounding, satisfying" },
    { activity: "Painting/Drawing", level: "Any", benefit: "Visual expression, no judgment" }
  ];

  const blocks = [
    { block: "'I'm not creative'", reframe: "Everyone is creative—it just looks different for each person" },
    { block: "Fear of failure", reframe: "There's no wrong way to do creative expression" },
    { block: "No time", reframe: "Even 10 minutes counts. Creativity is about presence, not perfection" },
    { block: "Comparison", reframe: "Your art doesn't need to be 'good'—it just needs to be yours" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-4">
            Week 11 • Day 1 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Creative Expression
          </h1>
          <p className="text-xl text-secondary-600">Unlock Your Creative Self</p>
        </div>

        <section className="bg-gradient-to-r from-violet-500 to-purple-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Palette className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Why Creativity Matters</h3>
              <p className="text-violet-100 text-lg">
                Creative expression reduces stress by 75%, improves problem-solving, boosts mood, 
                and connects you to your authentic self. This week, we play without judgment.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Creative Activities to Try</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {creativeActivities.map((item, index) => (
              <div key={index} className="p-4 bg-violet-50 rounded-xl">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold text-violet-700">{item.activity}</h3>
                  <span className="text-xs bg-violet-200 text-violet-700 px-2 py-1 rounded-full">{item.level}</span>
                </div>
                <p className="text-sm text-secondary-600">{item.benefit}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-8 border-2 border-violet-200">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-violet-500" />
            Overcoming Creative Blocks
          </h2>
          <div className="space-y-4">
            {blocks.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <p className="text-red-600 font-medium line-through mb-1">Block: "{item.block}"</p>
                <p className="text-green-700"><strong>Reframe:</strong> {item.reframe}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default CreativeExpressionGuide;
